import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import authService from "../services/authService";
import { supabase } from "../services/supabaseClient";
import { clearInfrastructureFailure, isInfrastructureError, reportInfrastructureFailure } from "../services/serviceStatusService";
// Firebase Messaging is intentionally loaded on demand. Keeping it out of the
// authentication bundle prevents Firebase from competing with the first paint.
const loadPushNotifications = () => import("../services/pushNotifications");

const AuthContext = createContext(null);

const safeDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const AUTH_REDIRECT_KEYS = [
  "uniair_redirect_after_auth",
  "uniair_last_private_route",
];

const normalizeAuthRedirectPath = (value = "") => {
  const raw = String(value || "").trim();

  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) {
    return null;
  }

  const pathname =
    raw.split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";

  const blockedPaths = [
    "/",
    "/login",
    "/register",
    "/signup",
    "/driver-register",
    "/forgot-password",
    "/verify-email",
    "/auth/callback",
    "/reset-password",
    "/student-email",
    "/complete-profile",
  ];

  const isPrivateRidePath =
    pathname === "/private-rides" || pathname.startsWith("/private-rides/");

  if (blockedPaths.includes(pathname) || isPrivateRidePath) {
    return null;
  }

  return raw;
};

const clearStoredAuthRedirects = () => {
  try {
    AUTH_REDIRECT_KEYS.forEach((key) => {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    });
  } catch {
    // Ignore storage errors.
  }
};

export const saveAuthRedirectPath = () => {
  try {
    const path = normalizeAuthRedirectPath(
      window.location.pathname + window.location.search
    );

    if (!path) {
      clearStoredAuthRedirects();
      return;
    }

    sessionStorage.setItem("uniair_redirect_after_auth", path);
    sessionStorage.removeItem("uniair_last_private_route");
    localStorage.removeItem("uniair_last_private_route");
  } catch (err) {
    console.warn("Could not save auth redirect path:", err);
  }
};

export const getSavedAuthRedirectPath = () => {
  try {
    const storedPath =
      sessionStorage.getItem("uniair_redirect_after_auth") ||
      sessionStorage.getItem("uniair_last_private_route") ||
      localStorage.getItem("uniair_redirect_after_auth") ||
      localStorage.getItem("uniair_last_private_route");

    const safePath = normalizeAuthRedirectPath(storedPath);

    if (!safePath) {
      clearStoredAuthRedirects();
      return null;
    }

    return safePath;
  } catch {
    return null;
  }
};

export const clearAuthRedirectPath = () => {
  clearStoredAuthRedirects();
};

export const AuthProvider = ({ children }) => {
  // The Supabase session and the UniAir profile are intentionally separate.
  // A profile/API request may fail temporarily without invalidating the session.
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);

  const [loading, setLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);

  const [notificationsEnabled, setNotificationsEnabled] = useState(
    typeof Notification !== "undefined" && Notification.permission === "granted"
  );

  const [verificationAccessState, setVerificationAccessState] = useState(null);
  const [verificationAccessLoading, setVerificationAccessLoading] =
    useState(false);

  const [adminAccess, setAdminAccess] = useState(null);
  const [adminAccessLoading, setAdminAccessLoading] = useState(false);

  const mountedRef = useRef(true);
  const sessionRef = useRef(null);
  const userRef = useRef(null);
  const profileErrorRef = useRef(null);
  const refreshInFlightRef = useRef(null);
  const verificationRefreshInFlightRef = useRef(null);
  const adminAccessRefreshInFlightRef = useRef(null);
  const postAuthSyncInFlightRef = useRef(null);
  const lastPostAuthSyncUserIdRef = useRef(null);
  const lastResumeRecoveryAtRef = useRef(0);

  const applySession = useCallback((nextSession) => {
    sessionRef.current = nextSession || null;
    if (mountedRef.current) setSession(nextSession || null);
  }, []);

  const clearProfileState = useCallback(() => {
    userRef.current = null;
    profileErrorRef.current = null;

    if (!mountedRef.current) return;

    setUser(null);
    setProfileError(null);
    setProfileLoading(false);
    setVerificationAccessState(null);
    setAdminAccess(null);
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    profileErrorRef.current = profileError;
  }, [profileError]);

  const refreshVerificationAccessState = useCallback(async () => {
    if (verificationRefreshInFlightRef.current) {
      return verificationRefreshInFlightRef.current;
    }

    const promise = (async () => {
      try {
        if (mountedRef.current) setVerificationAccessLoading(true);

        const {
          data: { session: currentSession },
          error: sessionErr,
        } = await supabase.auth.getSession();

        if (sessionErr) throw sessionErr;

        if (!currentSession?.user) {
          if (mountedRef.current) setVerificationAccessState(null);
          return null;
        }

        const { data, error } = await supabase.rpc(
          "get_my_verification_access_state"
        );

        if (error) throw error;

        if (mountedRef.current) {
          setVerificationAccessState(data || null);
        }

        return data || null;
      } catch (error) {
        // Preserve the most recent valid access state during a temporary outage.
        console.error("Error refreshing verification access state:", error);
        return null;
      } finally {
        if (mountedRef.current) setVerificationAccessLoading(false);
        verificationRefreshInFlightRef.current = null;
      }
    })();

    verificationRefreshInFlightRef.current = promise;
    return promise;
  }, []);

  const refreshAdminAccess = useCallback(async () => {
    if (adminAccessRefreshInFlightRef.current) {
      return adminAccessRefreshInFlightRef.current;
    }

    const promise = (async () => {
      try {
        if (mountedRef.current) setAdminAccessLoading(true);

        const {
          data: { session: currentSession },
          error: sessionErr,
        } = await supabase.auth.getSession();

        if (sessionErr) throw sessionErr;

        if (!currentSession?.user) {
          if (mountedRef.current) setAdminAccess(null);
          return null;
        }

        const { data, error } = await supabase.rpc("get_my_admin_access");

        if (error) throw error;

        if (mountedRef.current) {
          setAdminAccess(data || null);
        }

        return data || null;
      } catch (error) {
        // Preserve the most recent valid admin state during a temporary outage.
        console.error("Error refreshing admin access:", error);
        return null;
      } finally {
        if (mountedRef.current) setAdminAccessLoading(false);
        adminAccessRefreshInFlightRef.current = null;
      }
    })();

    adminAccessRefreshInFlightRef.current = promise;
    return promise;
  }, []);

  const refreshUser = useCallback(async () => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    const promise = (async () => {
      try {
        if (mountedRef.current) {
          setProfileLoading(true);
          setProfileError(null);
        }
        profileErrorRef.current = null;

        // Profile, student-verification access and staff access are independent.
        // Running them together removes two serial network round-trips from the
        // authenticated boot path.
        const [userData] = await Promise.all([
          authService.fetchCurrentUser(),
          refreshVerificationAccessState(),
          refreshAdminAccess(),
        ]);

        const {
          data: { session: currentSession },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError) throw sessionError;

        applySession(currentSession);

        if (!currentSession?.user) {
          clearProfileState();
          return null;
        }

        if (!userData?.id) {
          throw new Error("Your UniAir profile could not be loaded.");
        }

        userRef.current = userData;

        if (mountedRef.current) {
          setUser(userData);
          setProfileError(null);
        }

        return userData;
      } catch (error) {
        console.error("Error refreshing user profile:", error);

        let currentSession = sessionRef.current;

        try {
          const { data, error: sessionError } =
            await supabase.auth.getSession();
          if (!sessionError) currentSession = data?.session || null;
        } catch (sessionCheckError) {
          console.error("Could not re-check the auth session:", sessionCheckError);
        }

        applySession(currentSession);

        if (currentSession?.user) {
          // Critical fix: do not turn a profile/network failure into a logout.
          profileErrorRef.current = error;
          if (mountedRef.current) setProfileError(error);
          return userRef.current;
        }

        clearProfileState();
        return null;
      } finally {
        if (mountedRef.current) setProfileLoading(false);
        refreshInFlightRef.current = null;
      }
    })();

    refreshInFlightRef.current = promise;
    return promise;
  }, [
    applySession,
    clearProfileState,
    refreshVerificationAccessState,
    refreshAdminAccess,
  ]);

  const applyPendingSignupCodeIfAny = useCallback(async () => {
    const storeResult = (result) => {
      sessionStorage.setItem("signupCodeResult", JSON.stringify(result || {}));
      return result || null;
    };

    const claimStaged = async () => {
      const { data, error } = await supabase.rpc("claim_my_staged_signup_code_v1");
      if (error) throw error;
      return data || null;
    };

    let stagedResult = null;

    try {
      // The durable server-side intent is the source of truth. It survives
      // email verification on another browser or device.
      stagedResult = await claimStaged();
      if (stagedResult?.applied === true) {
        storeResult(stagedResult);
        sessionStorage.removeItem("pendingSignupCode");
        return stagedResult;
      }
    } catch (error) {
      // Keep the local fallback below for a safe migration from the previous
      // sessionStorage-only implementation and for temporary network failures.
      console.warn("Could not claim staged signup code yet:", error);
    }

    const code = (sessionStorage.getItem("pendingSignupCode") || "").trim();
    if (!code) return stagedResult;

    try {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      if (!session?.user?.id || !session?.user?.email) return stagedResult;

      // Recover a code that was captured by the old frontend but was not
      // staged before the signup redirect completed.
      const { error: stageError } = await supabase.rpc("stage_signup_code_intent_v1", {
        p_auth_user_id: session.user.id,
        p_email: session.user.email,
        p_code: code,
      });
      if (stageError) throw stageError;

      const staged = await claimStaged();
      if (staged?.applied === true) {
        storeResult(staged);
        sessionStorage.removeItem("pendingSignupCode");
        return staged;
      }

      throw new Error(staged?.message || "The signup code could not be linked to this account.");
    } catch (stageOrClaimError) {
      console.warn("Durable signup-code recovery failed:", stageOrClaimError);

      try {
        // Backward compatibility for referral/promo codes while deployments are
        // rolling out. Promoter codes require the durable staging functions.
        const { data, error } = await supabase.rpc("apply_signup_code", {
          p_code: code,
        });
        if (error) throw error;
        storeResult(data);
        sessionStorage.removeItem("pendingSignupCode");
        return data || null;
      } catch (legacyError) {
        console.error("Failed to apply signup code:", legacyError);
        const failedResult = {
          applied: false,
          error: legacyError?.message || "Failed to apply signup code",
        };
        storeResult(failedResult);
        // Keep the code for a later authenticated retry when the failure may be
        // a temporary deployment or connectivity issue.
        return failedResult;
      }
    }
  }, []);

  const completePendingStudentVerificationIfAny = useCallback(async () => {
    const pendingVerificationId = (
      sessionStorage.getItem("pendingStudentVerificationId") || ""
    ).trim();

    if (!pendingVerificationId) return;

    try {
      const {
        data: { session: currentSession },
        error: sessionErr,
      } = await supabase.auth.getSession();

      if (sessionErr) throw sessionErr;
      if (!currentSession?.user) return;

      const { data, error } = await supabase.rpc(
        "complete_student_verification_after_signup",
        {
          p_verification_id: pendingVerificationId,
        }
      );

      if (error) throw error;
      if (!data?.ok) throw new Error("Could not finalize student verification.");

      sessionStorage.removeItem("pendingStudentVerificationId");
    } catch (err) {
      console.error("Failed to complete deferred student verification:", err);
    }
  }, []);

  const runPostAuthSync = useCallback(async () => {
    if (postAuthSyncInFlightRef.current) {
      return postAuthSyncInFlightRef.current;
    }

    const promise = (async () => {
      const {
        data: { session: currentSession },
        error,
      } = await supabase.auth.getSession();

      if (error) throw error;
      applySession(currentSession);

      const currentUserId = currentSession?.user?.id || null;

      if (!currentUserId) {
        lastPostAuthSyncUserIdRef.current = null;
        clearProfileState();
        return null;
      }

      await completePendingStudentVerificationIfAny();

      // Load the profile once. Verification/admin state is fetched in parallel
      // inside refreshUser(), so the authenticated shell can become ready much
      // sooner than the previous serial boot path.
      const firstProfile = await refreshUser();
      if (!firstProfile?.id) return null;

      const hasLocalPendingSignupCode = Boolean(
        (sessionStorage.getItem("pendingSignupCode") || "").trim()
      );

      // A normal returning user should not wait for the durable signup-code
      // RPC before the authenticated shell can paint. When this browser has a
      // known pending code we still complete it synchronously; otherwise the
      // server-side recovery check runs just after the critical auth path.
      if (hasLocalPendingSignupCode) {
        const signupCodeResult = await applyPendingSignupCodeIfAny();

        if (signupCodeResult?.applied === true) {
          const refreshedProfile = await refreshUser();
          lastPostAuthSyncUserIdRef.current = currentUserId;
          return refreshedProfile;
        }
      } else {
        window.setTimeout(() => {
          applyPendingSignupCodeIfAny()
            .then((signupCodeResult) => {
              if (signupCodeResult?.applied === true) {
                return refreshUser();
              }
              return null;
            })
            .catch((signupCodeError) => {
              console.warn("Deferred signup-code sync failed:", signupCodeError);
            });
        }, 1800);
      }

      lastPostAuthSyncUserIdRef.current = currentUserId;
      return firstProfile;
    })().finally(() => {
      postAuthSyncInFlightRef.current = null;
    });

    postAuthSyncInFlightRef.current = promise;
    return promise;
  }, [
    applyPendingSignupCodeIfAny,
    applySession,
    clearProfileState,
    completePendingStudentVerificationIfAny,
    refreshUser,
  ]);

  const enablePushNotifications = async () => {
    if (!sessionRef.current?.user?.id) {
      throw new Error("You must be logged in first.");
    }

    const { requestPushPermissionAndSaveToken } = await loadPushNotifications();
    const token = await requestPushPermissionAndSaveToken();
    if (mountedRef.current) setNotificationsEnabled(true);
    return token;
  };

  const disablePushNotifications = async () => {
    try {
      const { disablePushTokenForCurrentUser } = await loadPushNotifications();
      await disablePushTokenForCurrentUser();
    } catch (err) {
      console.error("Failed to disable push token:", err);
    } finally {
      if (mountedRef.current) setNotificationsEnabled(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        if (mountedRef.current) setLoading(true);

        const {
          data: { session: storedSession },
          error,
        } = await supabase.auth.getSession();

        if (error) throw error;
        applySession(storedSession);

        if (storedSession?.user) {
          await runPostAuthSync();
        } else {
          clearProfileState();
        }
      } catch (error) {
        console.error("Auth initialization failed:", error);
        if (isInfrastructureError(error)) {
          reportInfrastructureFailure(error, "accounts");
        }
        profileErrorRef.current = error;
        if (mountedRef.current) setProfileError(error);
      } finally {
        if (!cancelled && mountedRef.current) {
          setLoading(false);
          setAuthInitialized(true);
        }
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [applySession, clearProfileState, runPostAuthSync]);

  useEffect(() => {
    if (typeof Notification === "undefined") {
      setNotificationsEnabled(false);
      return;
    }

    setNotificationsEnabled(Notification.permission === "granted");
  }, [session?.user?.id]);

  useEffect(() => {
    if (
      !session?.user?.id ||
      typeof Notification === "undefined" ||
      Notification.permission !== "granted"
    ) {
      return undefined;
    }

    let cancelled = false;

    // Push-token maintenance is useful but not paint-critical. Deferring the
    // Firebase import prevents it from taking bandwidth/CPU from LCP.
    const timer = window.setTimeout(async () => {
      try {
        const { syncPushTokenForCurrentUser } = await loadPushNotifications();
        if (cancelled) return;
        await syncPushTokenForCurrentUser();
      } catch (error) {
        if (!cancelled) {
          console.error("Could not refresh this device's push token:", error);
        }
      }
    }, 2500);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [session?.user?.id]);

  useEffect(() => {
    let unsubscribe = null;
    let cancelled = false;

    const timer = window.setTimeout(async () => {
      if (!session?.user?.id || cancelled) return;

      try {
        const { attachForegroundNotificationListener } =
          await loadPushNotifications();
        if (cancelled) return;
        unsubscribe = await attachForegroundNotificationListener();
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to attach foreground push listener:", err);
        }
      }
    }, 1800);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [session?.user?.id]);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      applySession(nextSession);

      if (event === "SIGNED_OUT") {
        lastPostAuthSyncUserIdRef.current = null;
        clearProfileState();

        if (mountedRef.current) {
          setNotificationsEnabled(false);
          setLoading(false);
          setAuthInitialized(true);
        }
        return;
      }

      const nextUserId = nextSession?.user?.id || null;
      const profileUserId = userRef.current?.id || null;
      const shouldRunPostAuthSync =
        event === "USER_UPDATED" ||
        (event === "SIGNED_IN" &&
          Boolean(nextUserId) &&
          (lastPostAuthSyncUserIdRef.current !== nextUserId ||
            profileUserId !== nextUserId ||
            Boolean(profileErrorRef.current)));

      if (shouldRunPostAuthSync) {
        window.setTimeout(() => {
          runPostAuthSync().catch((err) => {
            console.error("Post-auth sync failed:", err);
          });
        }, 0);
      }

      // Supabase may emit SIGNED_IN again when a browser tab regains focus.
      // Once the same user is already synced, that event must only update the
      // session object. Re-running profile and role RPCs would toggle access
      // loading flags and unmount active Admin workspaces.
      // TOKEN_REFRESHED follows the same lightweight path.
    });

    return () => {
      subscription?.unsubscribe?.();
    };
  }, [applySession, clearProfileState, runPostAuthSync]);

  useEffect(() => {
    const recoverAfterResume = async () => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState !== "visible"
      ) {
        return;
      }

      if (typeof navigator !== "undefined" && !navigator.onLine) return;

      const now = Date.now();
      if (now - lastResumeRecoveryAtRef.current < 15000) return;
      lastResumeRecoveryAtRef.current = now;

      try {
        const {
          data: { session: currentSession },
          error,
        } = await supabase.auth.getSession();

        if (error) throw error;
        applySession(currentSession);

        if (!currentSession?.user) {
          clearProfileState();
          return;
        }

        if (!userRef.current || profileErrorRef.current) {
          await refreshUser();
        }

        if (
          typeof Notification !== "undefined" &&
          Notification.permission === "granted"
        ) {
          const { syncPushTokenForCurrentUser } = await loadPushNotifications();
          await syncPushTokenForCurrentUser();
        }
      } catch (error) {
        console.error("App resume recovery failed:", error);
        if (isInfrastructureError(error)) {
          reportInfrastructureFailure(error, "accounts");
        }
      }
    };

    document.addEventListener("visibilitychange", recoverAfterResume);
    window.addEventListener("focus", recoverAfterResume);
    window.addEventListener("online", recoverAfterResume);

    return () => {
      document.removeEventListener("visibilitychange", recoverAfterResume);
      window.removeEventListener("focus", recoverAfterResume);
      window.removeEventListener("online", recoverAfterResume);
    };
  }, [applySession, clearProfileState, refreshUser]);

  const login = async (email, password) => {
    // Password and email verification are enforced by authService.login().
    // After that succeeds, profile/admin/verification refreshes are not a
    // second password login and must not turn a valid session into an outage.
    const result = await authService.login(email, password);
    const {
      data: { session: currentSession },
      error,
    } = await supabase.auth.getSession();

    if (error) throw error;
    if (!currentSession?.user?.id) {
      throw new Error("Could not confirm your UniAir session. Please try again.");
    }
    applySession(currentSession);

    // authService already fetched this exact user's protected profile.
    // Reuse it for the first authenticated paint instead of blocking the
    // login screen on another set of verification/admin database RPCs.
    // A failed profile lookup never grants staff/driver privileges: existing
    // route guards and backend RLS continue to verify those independently.
    if (
      !result?.profileUnavailable &&
      result?.user?.id === currentSession.user.id
    ) {
      userRef.current = result.user;
      profileErrorRef.current = null;
      if (mountedRef.current) {
        setUser(result.user);
        setProfileError(null);
      }
    }

    // A successful sign-in disproves this device's stale local login outage.
    clearInfrastructureFailure("accounts");

    // Login is complete; role/profile reconciliation can retry independently.
    // Failed optional work is reported in the authenticated profile recovery
    // UI, never as an invalid password or a global service outage.
    void runPostAuthSync().catch((syncError) => {
      console.warn("Post-login profile sync will need retry:", syncError);
      profileErrorRef.current = syncError;
      if (mountedRef.current) setProfileError(syncError);
    });

    return result;
  };

  const logout = async () => {
    try {
      const { disablePushTokenForCurrentUser } = await loadPushNotifications();
      await disablePushTokenForCurrentUser();
    } catch (err) {
      console.error("Failed to deactivate push token on logout:", err);
    }

    await authService.logout();
    applySession(null);
    clearProfileState();

    if (mountedRef.current) {
      setNotificationsEnabled(false);
      setLoading(false);
      setAuthInitialized(true);
    }
  };

  const register = async (userData) => {
    const data = await authService.register(userData);
    return data;
  };

  const userType = useMemo(
    () => (user?.user_type || "").toLowerCase(),
    [user?.user_type]
  );

  const isDriver = userType === "driver";
  const isProfileAdmin = userType === "admin";

  const isAnyAdmin = !!adminAccess?.is_any_admin;
  const isSuperAdmin = !!adminAccess?.is_superadmin;
  const isFinanceAdmin = !!adminAccess?.is_finance;
  const isMarketing = !!adminAccess?.is_marketing;
  const isMarketingLead = !!adminAccess?.is_marketing_lead;
  const isPromoter = !!adminAccess?.is_promoter;

  const isAdmin = isAnyAdmin || isProfileAdmin;

  const studentVerificationExpiresAt = useMemo(
    () => verificationAccessState?.student_verification_expires_at || null,
    [verificationAccessState]
  );

  const studentVerificationExpiresAtDate = useMemo(
    () => safeDate(studentVerificationExpiresAt),
    [studentVerificationExpiresAt]
  );

  const studentVerifiedAt = useMemo(
    () => verificationAccessState?.student_verified_at || null,
    [verificationAccessState]
  );

  const studentVerificationStatus = useMemo(
    () => verificationAccessState?.student_verification_status || "unverified",
    [verificationAccessState]
  );

  const studentEmailConfirmed = useMemo(
    () => !!verificationAccessState?.student_email_confirmed,
    [verificationAccessState]
  );

  const requiresStudentReverification = useMemo(
    () => !!verificationAccessState?.student_reverification_required,
    [verificationAccessState]
  );

  const canBookTrips = useMemo(
    () => !!verificationAccessState?.can_book_trips,
    [verificationAccessState]
  );

  const canSetDriverAvailability = useMemo(
    () => !!verificationAccessState?.can_set_driver_availability,
    [verificationAccessState]
  );

  const driverIdentityRolloutMode = useMemo(
    () => verificationAccessState?.driver_identity_rollout_mode || "off",
    [verificationAccessState]
  );

  const driverIdentityRequired = useMemo(
    () => !!verificationAccessState?.driver_identity_required,
    [verificationAccessState]
  );

  const driverIdentityEnrolled = useMemo(
    () => !!verificationAccessState?.driver_identity_enrolled,
    [verificationAccessState]
  );

  const driverIdentityStatus = useMemo(
    () => verificationAccessState?.driver_identity_status || null,
    [verificationAccessState]
  );

  const driverIdentityHasBootstrapReference = useMemo(
    () => !!verificationAccessState?.driver_identity_has_bootstrap_reference,
    [verificationAccessState]
  );

  const driverIdentityManualReviewRequired = useMemo(
    () => !!verificationAccessState?.driver_identity_manual_review_required,
    [verificationAccessState]
  );

  const driverIdentityLockedUntil = useMemo(
    () => verificationAccessState?.driver_identity_locked_until || null,
    [verificationAccessState]
  );

  const driverIdentityLastVerifiedAt = useMemo(
    () => verificationAccessState?.driver_identity_last_verified_at || null,
    [verificationAccessState]
  );

  const driverIdentitySessionExpiresAt = useMemo(
    () => verificationAccessState?.driver_identity_session_expires_at || null,
    [verificationAccessState]
  );

  const driverIdentityVerificationRequiredNow = useMemo(
    () => !!verificationAccessState?.driver_identity_verification_required_now,
    [verificationAccessState]
  );

  const canAccessDriverWorkspace = useMemo(
    () => !!verificationAccessState?.can_access_driver_workspace,
    [verificationAccessState]
  );

  const studentVerificationIsActive = useMemo(() => {
    return (
      studentEmailConfirmed &&
      studentVerificationStatus === "verified" &&
      !requiresStudentReverification
    );
  }, [
    studentEmailConfirmed,
    studentVerificationStatus,
    requiresStudentReverification,
  ]);

  const studentVerificationDaysRemaining = useMemo(() => {
    if (!studentVerificationExpiresAtDate) return null;
    const diffMs = studentVerificationExpiresAtDate.getTime() - Date.now();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  }, [studentVerificationExpiresAtDate]);

  const studentVerificationIsDueSoon = useMemo(() => {
    return (
      studentVerificationIsActive &&
      typeof studentVerificationDaysRemaining === "number" &&
      studentVerificationDaysRemaining <= 30
    );
  }, [studentVerificationIsActive, studentVerificationDaysRemaining]);

  const authUser = session?.user || null;
  const isAuthenticated = !!authUser;
  const authReady = !loading && authInitialized;

  const value = {
    session,
    authUser,
    user,

    login,
    logout,
    register,
    refreshUser,
    retryProfile: refreshUser,
    refreshVerificationAccessState,
    refreshAdminAccess,

    profileLoading,
    profileError,

    adminAccess,
    adminAccessLoading,

    isAuthenticated,
    isDriver,
    isAdmin,
    isAnyAdmin,
    isSuperAdmin,
    isFinanceAdmin,
    isMarketing,
    isMarketingLead,
    isPromoter,
    isProfileAdmin,

    loading,
    authReady,
    authInitialized,
    verificationAccessLoading,

    verificationAccessState,
    studentVerificationStatus,
    studentEmailConfirmed,
    studentVerifiedAt,
    studentVerificationExpiresAt,
    studentVerificationExpiresAtDate,
    studentVerificationIsActive,
    studentVerificationIsDueSoon,
    studentVerificationDaysRemaining,
    requiresStudentReverification,
    canBookTrips,
    canSetDriverAvailability,

    driverIdentityRolloutMode,
    driverIdentityRequired,
    driverIdentityEnrolled,
    driverIdentityStatus,
    driverIdentityHasBootstrapReference,
    driverIdentityManualReviewRequired,
    driverIdentityLockedUntil,
    driverIdentityLastVerifiedAt,
    driverIdentitySessionExpiresAt,
    driverIdentityVerificationRequiredNow,
    canAccessDriverWorkspace,

    notificationsEnabled,
    enablePushNotifications,
    disablePushNotifications,

    saveAuthRedirectPath,
    getSavedAuthRedirectPath,
    clearAuthRedirectPath,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
